/* ==========================================================================
   Admin Dashboard Page
   Task F31: Admin dashboard overview with stats and quick actions
   ========================================================================== */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useAdminAuth, useAdminPermissions } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import {
  listProviders,
  listCatalog,
  listPrescriptions,
  inviteProvider,
  createProduct,
} from '@/api/admin'
import { ApiClientError } from '@/api/client'
import { InviteProviderModal } from '@/components/admin/InviteProviderModal'
import { isCanada } from '@/utils/countries'
import { ProductFormModal } from '@/components/admin/ProductFormModal'
import type {
  ProviderInvite,
  CatalogProductCreate,
  CatalogProductUpdate,
} from '@/api/types'

/* --------------------------------------------------------------------------
   Stats Card Component
   -------------------------------------------------------------------------- */

interface StatsCardProps {
  title: string
  value: number | string
  subtitle?: string
  loading?: boolean
  icon: React.ReactNode
  onClick?: () => void
}

function StatsCard({
  title,
  value,
  subtitle,
  loading,
  icon,
  onClick,
}: StatsCardProps) {
  const content = (
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        {loading ? (
          <div className="h-8 w-16 bg-gray-200 rounded animate-pulse mt-1" />
        ) : (
          <p className="text-3xl font-semibold text-charcoal mt-1">{value}</p>
        )}
        {subtitle && !loading && (
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        )}
      </div>
      <div className="p-3 bg-lavender/10 rounded-lg text-plum">{icon}</div>
    </div>
  )

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 sm:p-6 cursor-pointer hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-lavender"
      >
        {content}
      </button>
    )
  }

  return <Card>{content}</Card>
}

/* --------------------------------------------------------------------------
   Icons
   -------------------------------------------------------------------------- */

function UsersIcon() {
  return (
    <svg
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  )
}

function PackageIcon() {
  return (
    <svg
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
      />
    </svg>
  )
}

function DocumentIcon() {
  return (
    <svg
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
      />
    </svg>
  )
}

function UserPlusIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
      />
    </svg>
  )
}

/* --------------------------------------------------------------------------
   Dashboard Component
   -------------------------------------------------------------------------- */

interface DashboardStats {
  totalProviders: number
  activeProviders: number
  totalProducts: number
  activeProducts: number
  totalPrescriptions: number
}

export function Dashboard() {
  const navigate = useNavigate()
  const { pharmacy } = useAdminAuth()
  const { canManage } = useAdminPermissions()
  const { error: showError, success: showSuccess } = useToast()

  // Stats state
  const [stats, setStats] = useState<DashboardStats>({
    totalProviders: 0,
    activeProviders: 0,
    totalProducts: 0,
    activeProducts: 0,
    totalPrescriptions: 0,
  })
  const [loading, setLoading] = useState(true)

  // Modal state
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showProductModal, setShowProductModal] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [productLoading, setProductLoading] = useState(false)

  const slug = pharmacy?.portal_slug || ''

  // Fetch all stats
  const fetchStats = useCallback(async () => {
    if (!slug) return

    setLoading(true)
    try {
      // Fetch data in parallel. Staff (PORTAL-94) cannot view prescriptions, so
      // skip that call for them (it would 403) — their prescriptions stat is hidden.
      const [providersData, productsData, prescriptionsData] = await Promise.all([
        listProviders(slug),
        listCatalog(slug),
        canManage ? listPrescriptions(slug, { limit: 1 }) : Promise.resolve(null),
      ])

      // Count active providers
      const activeProviders = providersData.providers.filter(
        (p) => p.status === 'active'
      ).length

      // Count active products
      const activeProducts = productsData.products.filter((p) => p.is_active).length

      setStats({
        totalProviders: providersData.total,
        activeProviders,
        totalProducts: productsData.total,
        activeProducts,
        totalPrescriptions: prescriptionsData?.total ?? 0,
      })
    } catch (err) {
      if (err instanceof ApiClientError) {
        showError(err.getUserMessage())
      } else {
        showError('Failed to load dashboard stats')
      }
    } finally {
      setLoading(false)
    }
  }, [slug, showError, canManage])

  // Load stats on mount
  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  // Handle provider invitation
  async function handleInviteProvider(data: ProviderInvite): Promise<void> {
    if (!slug) return

    setInviteLoading(true)
    try {
      await inviteProvider(slug, data)
      setShowInviteModal(false)
      showSuccess('Provider invitation sent successfully!')
      fetchStats() // Refresh stats
    } catch (err) {
      if (err instanceof ApiClientError) {
        showError(err.getUserMessage())
      } else {
        showError('Failed to send invitation')
      }
      throw err // Re-throw to let modal handle error state
    } finally {
      setInviteLoading(false)
    }
  }

  // Handle product creation
  async function handleCreateProduct(
    data: CatalogProductCreate | CatalogProductUpdate
  ): Promise<void> {
    if (!slug) return

    setProductLoading(true)
    try {
      // Since we're creating (product is null), data is always CatalogProductCreate
      await createProduct(slug, data as CatalogProductCreate)
      setShowProductModal(false)
      showSuccess('Product created successfully!')
      fetchStats() // Refresh stats
    } catch (err) {
      if (err instanceof ApiClientError) {
        showError(err.getUserMessage())
      } else {
        showError('Failed to create product')
      }
      throw err // Re-throw to let modal handle error state
    } finally {
      setProductLoading(false)
    }
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-6xl">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-charcoal">
            Welcome back{pharmacy?.name ? `, ${pharmacy.name}` : ''}
          </h1>
          <p className="text-gray-600 mt-2">
            Here's an overview of your pharmacy portal.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatsCard
            title="Total Providers"
            value={stats.totalProviders}
            subtitle={`${stats.activeProviders} active`}
            loading={loading}
            icon={<UsersIcon />}
            onClick={() => navigate(`/rx/${slug}/admin/providers`)}
          />
          <StatsCard
            title="Active Products"
            value={stats.activeProducts}
            subtitle={`${stats.totalProducts} total`}
            loading={loading}
            icon={<PackageIcon />}
            onClick={() => navigate(`/rx/${slug}/admin/catalog`)}
          />
          {canManage && (
            <StatsCard
              title="Prescriptions"
              value={stats.totalPrescriptions}
              subtitle="All time"
              loading={loading}
              icon={<DocumentIcon />}
            />
          )}
        </div>

        {/* Quick Actions */}
        <Card>
          <h2 className="font-serif text-xl text-charcoal mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-4">
            <Button variant="primary" onClick={() => setShowInviteModal(true)}>
              <UserPlusIcon />
              <span className="ml-2">Invite Provider</span>
            </Button>
            {/* Staff (PORTAL-94) can't manage the formulary or settings — hide these
                CTAs so they don't hit a 403 / redirect dead-end from their dashboard. */}
            {canManage && (
              <Button variant="secondary" onClick={() => setShowProductModal(true)}>
                <PlusIcon />
                <span className="ml-2">Add Product</span>
              </Button>
            )}
            {canManage && (
              <Button
                variant="secondary"
                onClick={() => navigate(`/rx/${slug}/admin/settings`)}
              >
                Portal Settings
              </Button>
            )}
          </div>
        </Card>

        {/* Getting Started Guide - Show if stats are low */}
        {!loading &&
          (stats.totalProviders === 0 || stats.totalProducts === 0) && (
            <Card className="mt-6 bg-lavender/5 border-lavender/20">
              <h2 className="font-serif text-xl text-charcoal mb-3">
                Getting Started
              </h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div
                    className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                      stats.totalProducts > 0
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {stats.totalProducts > 0 ? '✓' : '1'}
                  </div>
                  <div>
                    <p
                      className={`font-medium ${stats.totalProducts > 0 ? 'text-gray-500' : 'text-charcoal'}`}
                    >
                      Add products to your catalog
                    </p>
                    <p className="text-sm text-gray-500">
                      Create the medications and compounds your providers can
                      prescribe.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div
                    className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                      stats.totalProviders > 0
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {stats.totalProviders > 0 ? '✓' : '2'}
                  </div>
                  <div>
                    <p
                      className={`font-medium ${stats.totalProviders > 0 ? 'text-gray-500' : 'text-charcoal'}`}
                    >
                      Invite providers
                    </p>
                    <p className="text-sm text-gray-500">
                      Send invitations to healthcare providers who will use the
                      portal.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium bg-gray-100 text-gray-600">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-charcoal">
                      Share your portal link
                    </p>
                    <p className="text-sm text-gray-500">
                      Providers can access your portal at{' '}
                      <code className="bg-gray-100 px-1 rounded text-xs">
                        {window.location.origin}/rx/{slug}
                      </code>
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}
      </div>

      {/* Invite Provider Modal */}
      <InviteProviderModal
        showNpi={!isCanada(pharmacy?.country)}
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSubmit={handleInviteProvider}
        loading={inviteLoading}
      />

      {/* Product Form Modal */}
      <ProductFormModal
        open={showProductModal}
        onClose={() => setShowProductModal(false)}
        onSubmit={handleCreateProduct}
        product={null}
        loading={productLoading}
        country={pharmacy?.country}
      />
    </div>
  )
}

export default Dashboard
